import { Injectable } from "@angular/core";
import { ObgConfigFeAdaptiveClientCore } from "@obg/core.config";
import { ConfigService } from "@obg/ngx.global";
import { Observable } from "rxjs";

import {
	COOL_OFF_DEFAULT_TIMOUTS,
	INDEFINITE_SELF_EXCLUSION_DEFAULT_INTERVAL,
	PANIC_BUTTON_DEFAULT_TIMEOUT,
	SELF_EXCLUSION_DEFAULT_TIMEOUTS,
} from "./responsible-gaming.const";
import { Timeout, TimeoutMinutes } from "./responsible-gaming.model";

@Injectable({
	providedIn: "root",
})
export class ResponsibleGamingConfigService {
	constructor(private configService: ConfigService<ObgConfigFeAdaptiveClientCore>) {}

	getSelfExclusionEndpoint(): string {
		return this.configService.get(
			(x) => x.core.responsibleGaming.endpoints!.selfExclusion,
			"v1/currentcustomerlimits/selfexclusion/"
		);
	}

	getInstantSelfExclusionEndpoint(): string {
		return this.configService.get(
			(x) => x.core.responsibleGaming.endpoints!.instantSelfExclusion,
			"v1/currentcustomerlimits/selfexclusion/minutes/"
		);
	}

	getCoolOffPeriodEndpoint(): string {
		return this.configService.get(
			(x) => x.core.responsibleGaming.endpoints!.coolOffPeriod,
			"v1/currentcustomerlimits/selfexclusion/"
		);
	}

	getNotificationResultEndpoint(): string {
		return this.configService.get(
			(x) => x.core.responsibleGaming.endpoints!.notificationResult,
			"v1/responsiblegaming/notification-result/"
		);
	}

	getProductBlockEndpoint(): string {
		return this.configService.get(
			(x) => x.core.responsibleGaming.endpoints!.productBlock,
			"v1/ClassificationBlocks"
		);
	}

	/**
	 * This config is under jurisdiction
	 */
	getTimeOffEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.timeOffEnabled, false);
	}

	getTimeOffWebComponentEnabled(): boolean {
		return this.configService.get((conf) => conf.core.responsibleGaming.timeOffWebComponentEnabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getIsLoginAlertEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.loginAlert!.enabled, false);
	}

	getIsLoginLimitAlertEnabled$(): Observable<boolean | undefined> {
		return this.configService.get$((x) => x.core.responsibleGaming.loginAlert?.loginLimitAlert?.enabled, false);
	}

	getLoginLimitAlertLimit(): number {
		return this.configService.get((x) => x.core.responsibleGaming.loginAlert?.loginLimitAlert?.limit, 10);
	}

	getLoginLimitAlertPeriodMinutes(): number {
		return this.configService.get((x) => x.core.responsibleGaming.loginAlert?.loginLimitAlert?.periodMinutes, 60);
	}

	/**
	 * This config is under jurisdiction
	 */
	getSessionLimitIsEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.sessionLimit!.isEnabled, true);
	}

	/**
	 * This config is under jurisdiction
	 */
	getSessionLimitIsRequired(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.sessionLimit!.isBlockingProductEnabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getSessionLimitIsRequired$(): Observable<boolean> {
		return this.configService.get$(
			(x) => x.core.responsibleGaming.sessionLimit?.isBlockingProductEnabled ?? false,
			false
		);
	}

	/**
	 * This config is under jurisdiction
	 */
	getCoolOffTimeouts(): (Timeout | TimeoutMinutes)[] {
		return this.configService.get((x) => x.core.responsibleGaming.coolOff!.timeouts, COOL_OFF_DEFAULT_TIMOUTS) as (
			| Timeout
			| TimeoutMinutes
		)[];
	}

	/**
	 * This config is under jurisdiction
	 */
	getSelfExclusionTimeouts(): Timeout[] {
		return this.configService.get(
			(x) => x.core.responsibleGaming.selfExclusion!.timeouts,
			SELF_EXCLUSION_DEFAULT_TIMEOUTS
		) as Timeout[];
	}

	/**
	 * This config is under jurisdiction
	 */
	getIndefiniteSelfExclusionInterval(): Timeout[] {
		return this.configService.get(
			(x) => x.core.responsibleGaming.selfExclusion!.indefiniteTimeoutInterval,
			INDEFINITE_SELF_EXCLUSION_DEFAULT_INTERVAL
		) as Timeout[];
	}

	/**
	 * This config is under jurisdiction
	 */
	getSelfExclusionTimeoutsInclusive(): Timeout[] {
		return this.configService.get(
			(x) => x.core.responsibleGaming.selfExclusion!.timeoutsInclusive,
			SELF_EXCLUSION_DEFAULT_TIMEOUTS
		) as Timeout[];
	}

	/**
	 * This config is under jurisdiction
	 */
	getIsSelfExclusionWarningShown(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.selfExclusion!.showWarningDialog, false);
	}

	getIsSelfExclusionInfoTextShown(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.selfExclusion!.infoText, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getAsaEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.asaEnabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getHeaderSelfAssessmentButtonEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.headerSelfAssessmentButton!.isEnabled, true);
	}

	/**
	 * This config is under jurisdiction
	 */
	getHeaderGameLimitsButtonEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.headerGameLimitsButton!.isEnabled, true);
	}

	/**
	 * This config is under jurisdiction
	 */
	getHeaderPanicButtonEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.headerPanicButton!.isEnabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getHeaderGamblingSummaryButtonEnabled$(): Observable<boolean | undefined> {
		return this.configService.get$((x) => x.core.responsibleGaming.regulatoryBar?.gamblingSummaryEnabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	showPanicButtonClickedMessage(): boolean {
		return this.configService.get(
			(x) => x.core.responsibleGaming.headerPanicButton!.showMessageAfterClicked,
			false
		);
	}

	/**
	 * This config is under jurisdiction
	 */
	getFooterPanicButtonEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.footerPanicButton!.isEnabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getPanicButtonTimeout(): Timeout {
		return this.configService.get(
			(x) => x.core.responsibleGaming.panicButton!.timeout,
			PANIC_BUTTON_DEFAULT_TIMEOUT
		) as Timeout;
	}

	/**
	 * This config is under jurisdiction
	 */
	getPanicButtonIsInstant(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.panicButton!.isInstant, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getTwelveHourSelfExclusion(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.panicButton!.twelveHourSelfExclusion, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getPanicButtonHoverIsEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.panicButton!.hover!.isEnabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getPanicButtonHoverDurationInMs(): number {
		return this.configService.get((x) => x.core.responsibleGaming.panicButton!.hover!.durationInMs, 0);
	}

	/**
	 * This config is under jurisdiction
	 */
	getRegulatoryBarEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.regulatoryBar!.enabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getRegulatoryBarEnabled$(): Observable<boolean | undefined> {
		return this.configService.get$((x) => x.core.responsibleGaming.regulatoryBar?.enabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getRegulatoryBarEnabledForLoggedOut$(): Observable<boolean | undefined> {
		return this.configService.get$((x) => x.core.responsibleGaming.regulatoryBar?.loggedOut, true);
	}

	/**
	 * This config is under jurisdiction
	 */
	getCrossBrandExclusionVisible(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.isCrossBrandExclusionVisible, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getInteractionsEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.interactions!.enabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getCasinoBlockEnabled(): boolean {
		return this.configService.get((x) => x.core.responsibleGaming.isCasinoBlockEnabled, false);
	}

	/**
	 * This config is under jurisdiction
	 */
	getSessionSummaryEnabled$(): Observable<boolean | undefined> {
		return this.configService.get$((x) => x.core.responsibleGaming.sessionSummaryEnabled, false);
	}

	getNetlossLimitWebComponentEnabled(): boolean {
		return this.configService.get((conf) => conf.core.responsibleGaming.netlossLimitWebComponentEnabled, false);
	}

	getBettingLimitWebComponentEnabled(): boolean {
		return this.configService.get((conf) => conf.core.responsibleGaming.bettingLimitWebComponentEnabled, false);
	}

	getProvidersWithHiddenCodes(): Record<string, string[]> {
		return this.configService.get((conf) => conf.core.responsibleGaming.providersWithHiddenCodes, {});
	}
}
